import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, EyeOff, Lock, Trash2 } from "lucide-react";

/**
 * PrivacyTrust — surfaces the privacy promise on the landing page.
 * Critical for an intimate AI coach where users share personal details.
 */
export default function PrivacyTrust() {
  return (
    <section
      data-testid="privacy-trust-section"
      className="relative py-20 md:py-24 bg-[#f7f3ea] border-t border-[#eadfc9]"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="font-mono-eyebrow text-[#3e6b52] mb-3">Built for what you'll actually share</p>
          <h2 className="font-display text-[32px] sm:text-[40px] md:text-[52px] leading-[1.05] text-[#0f1724] tracking-tight">
            Your conversations stay{" "}
            <span className="italic font-light text-[#3e6b52]">yours</span>.
          </h2>
          <p className="font-body text-[15px] md:text-[16px] text-[#4b5563] mt-4 max-w-2xl mx-auto leading-relaxed">
            Ascend is a place to be honest — about money pressure, your relationships, your fears,
            your wins. We treat that like a vault. Only you hold the key.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: ShieldCheck,
              title: "Never sold",
              body: "Not to advertisers. Not to data brokers. Not to anyone — ever.",
            },
            {
              icon: EyeOff,
              title: "Never shared",
              body: "Your chats aren't used to train public AI, posted publicly, or shown to other users.",
            },
            {
              icon: Lock,
              title: "Encrypted in transit",
              body: "TLS 1.2+. Stored on hardened cloud infra. Restricted, audited access.",
            },
            {
              icon: Trash2,
              title: "Delete anytime",
              body: "One click. No forms. We delete your data within 30 days of the request.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl bg-white border border-[#eadfc9] p-5 md:p-6"
            >
              <div className="w-10 h-10 rounded-full bg-[#e9f0ec] flex items-center justify-center mb-4">
                <Icon size={18} className="text-[#3e6b52]" />
              </div>
              <p className="font-display text-[18px] text-[#0f1724] leading-tight mb-2">{title}</p>
              <p className="font-body text-[13px] text-[#4b5563] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center max-w-3xl mx-auto">
          <p className="font-body text-[13px] text-[#4b5563] leading-relaxed italic">
            The only times we will ever hand over your data are if{" "}
            <span className="not-italic font-medium text-[#0f1724]">you ask us to</span>, or if we are{" "}
            <span className="not-italic font-medium text-[#0f1724]">legally compelled</span> by a court order or
            credible imminent-harm safety report. Even then, only what's required.{" "}
            <Link to="/privacy" className="text-[#486a8f] underline hover:text-[#3a5a7c]">
              Read the full Privacy Promise →
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
